import React, { useEffect, useState } from "react";
import { Form, Input, Button, Flex, Card, AutoComplete } from 'antd';
import { DeleteOutlined } from "@ant-design/icons";
import * as actions from "store/actions/AuthActions";

// Must match _normalise_extra_key() in wsgi/teson/lead/imports.py, so a field
// typed by hand lands on the same key the importer would produce.
export const normaliseExtraKey = (raw) =>
    String(raw ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

// 'budget_range' -> 'Budget Range'
export const extraKeyLabel = (key) =>
    String(key ?? '').split('_').filter(Boolean).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

// extra_data is an object; Form.List needs an array
export const extraDataToRows = (extraData) =>
    Object.entries(extraData || {}).map(([key, value]) => ({ key, value }));

// ...and back again for the API
export const rowsToExtraData = (rows) =>
    (rows || []).reduce((acc, row) => {
        const key = normaliseExtraKey(row?.key);
        if (key) acc[key] = row?.value ?? '';
        return acc;
    }, {});

/**
 * "Additional Information" key/value rows for a record's `extra_data`, bound
 * to the `extra_fields` field of the surrounding Form.
 *
 * `savedKeys` are the keys already stored on the record, in the same order as
 * the rows seeded into `extra_fields`. Those render as fixed labels and cannot
 * be removed; rows added in this session get a key dropdown fed by the shared
 * key master (lead/extra-field-keys), where the user can pick or type a key.
 */
const ExtraFieldsList = ({ savedKeys = [] }) => {
    const form = Form.useFormInstance();
    const rows = Form.useWatch('extra_fields', form);
    const [masterKeys, setMasterKeys] = useState([]);

    // Reloaded when savedKeys changes, i.e. after a save may have added keys.
    useEffect(() => {
        let active = true;
        actions.listLeadExtraFieldKeys().then((keys) => { if (active) setMasterKeys(keys); });
        return () => { active = false; };
    }, [savedKeys]);

    // Dropdown options for the key of row `index`: master keys not already
    // used by another row, matched against what is typed.
    const keyOptions = (index) => {
        const current = rows || [];
        const used = new Set(
            current.filter((_, i) => i !== index).map((row) => normaliseExtraKey(row?.key)).filter(Boolean)
        );
        const typed = String(current[index]?.key ?? '').trim().toLowerCase();
        return masterKeys
            .filter((item) => !used.has(item.key))
            .filter((item) => !typed
                || (item.label || '').toLowerCase().includes(typed)
                || item.key.includes(normaliseExtraKey(typed)))
            .map((item) => ({ value: item.label || extraKeyLabel(item.key), label: item.label || extraKeyLabel(item.key) }));
    };

    return (
        <Form.List name="extra_fields">
            {(fields, { add, remove }) => (
                // Nothing captured and nothing added yet: show only a
                // quiet link rather than an empty card.
                fields.length === 0 ? (
                    <Button type="link" onClick={() => add({ key: '', value: '' })} style={{ paddingLeft: 24 }}>
                        + Add field
                    </Button>
                ) : (
                    <Card title="Additional Information" bordered={false}>
                        {fields.map((field) => {
                            // Saved rows are loaded first and cannot be removed, so
                            // they always occupy the leading indices - anything past
                            // them was added in this session.
                            const isSaved = field.name < savedKeys.length;
                            return isSaved ? (
                                // Already saved: label is fixed, value only.
                                // The key is kept as a hidden field because onFinish
                                // only returns registered fields - drop it and the
                                // field would be wiped on save.
                                <React.Fragment key={field.key}>
                                    <Form.Item name={[field.name, 'key']} hidden>
                                        <Input />
                                    </Form.Item>
                                    <Form.Item
                                        label={extraKeyLabel(savedKeys[field.name])}
                                        name={[field.name, 'value']}
                                    >
                                        <Input />
                                    </Form.Item>
                                </React.Fragment>
                            ) : (
                                // Added in this session: name editable, removable
                                <Flex key={field.key} gap="small" align="baseline" style={{ marginBottom: 8 }}>
                                    <Form.Item
                                        name={[field.name, 'key']}
                                        style={{ flex: 1, marginBottom: 0 }}
                                        rules={[
                                            { required: true, message: 'Enter a field name' },
                                            {
                                                // Two rows on one key would silently overwrite each other on save
                                                validator: (_, value) => {
                                                    const key = normaliseExtraKey(value);
                                                    const clash = (form.getFieldValue('extra_fields') || [])
                                                        .some((row, i) => i !== field.name && normaliseExtraKey(row?.key) === key);
                                                    return key && clash
                                                        ? Promise.reject(new Error('This field is already added'))
                                                        : Promise.resolve();
                                                },
                                            },
                                        ]}
                                    >
                                        {/* Pick an existing key (e.g. Instagram) or type a new one */}
                                        <AutoComplete
                                            placeholder="Field name"
                                            options={keyOptions(field.name)}
                                        />
                                    </Form.Item>
                                    <Form.Item name={[field.name, 'value']} style={{ flex: 1, marginBottom: 0 }}>
                                        <Input placeholder="Value" />
                                    </Form.Item>
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => remove(field.name)}
                                    />
                                </Flex>
                            );
                        })}
                        <Button type="link" onClick={() => add({ key: '', value: '' })} style={{ paddingLeft: 0 }}>
                            + Add field
                        </Button>
                    </Card>
                )
            )}
        </Form.List>
    );
};

export default ExtraFieldsList;
